/**
 * Scroll↔selection sync for the transcript outline.
 *
 * Tracks which transcript node is scrolled into view and reflects it as the
 * outline selection (via the nearest outline row at or above the scrolled
 * node). Outline clicks scroll the transcript programmatically; while that
 * scroll settles, tracking is suppressed so the selection doesn't flicker
 * through intermediate rows.
 */

import { useCallback, useMemo, useRef, type RefObject } from "react";

import { useScrollTrack } from "@tsmono/react/hooks";

import { EventNode } from "../types";

const kFramesToStabilize = 10;

/**
 * Find the nearest outline row at or above `targetId` in flat-list order.
 * Returns null when the target is not in the list or no outline row
 * precedes it.
 */
export function findNearestOutlineAbove(
  targetId: string,
  allNodesList: EventNode[],
  outlineIds: ReadonlySet<string>
): EventNode | null {
  const targetIndex = allNodesList.findIndex((node) => node.id === targetId);
  if (targetIndex === -1) return null;

  for (let i = targetIndex; i >= 0; i--) {
    const node = allNodesList[i];
    if (node !== undefined && node.id) {
      if (outlineIds.has(node.id)) {
        return node;
      }
    }
  }

  return null;
}

export interface UseOutlineScrollSyncOptions {
  /** Full flattened node list (unfiltered), in transcript order. */
  allNodesList: EventNode[];
  /** Rows displayed in the outline. */
  outlineNodeList: EventNode[];
  /** The transcript's scroll container. */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Offset from the top of the scroll container where visible content begins. */
  scrollTrackOffset?: number;
  /** Set the selected outline node ID. */
  setSelectedOutlineId?: (id: string) => void;
}

export interface OutlineScrollSync {
  /** Click handler for outline rows: selects the row and suppresses scroll
   *  tracking until the resulting programmatic scroll stabilizes. */
  onOutlineSelect: (nodeId: string) => void;
}

export function useOutlineScrollSync(
  options: UseOutlineScrollSyncOptions
): OutlineScrollSync {
  const {
    allNodesList,
    outlineNodeList,
    scrollRef,
    scrollTrackOffset,
    setSelectedOutlineId,
  } = options;

  // Flag to indicate programmatic scrolling is in progress.
  const isProgrammaticScrolling = useRef(false);
  const lastScrollPosition = useRef<number | null>(null);
  const stableFrameCount = useRef(0);

  const beginProgrammaticScroll = useCallback(() => {
    isProgrammaticScrolling.current = true;
    lastScrollPosition.current = null;
    stableFrameCount.current = 0;

    const checkScrollStabilized = () => {
      if (!isProgrammaticScrolling.current) return;

      const currentPosition = scrollRef?.current?.scrollTop ?? null;

      if (currentPosition === lastScrollPosition.current) {
        stableFrameCount.current++;
        if (stableFrameCount.current >= kFramesToStabilize) {
          isProgrammaticScrolling.current = false;
          return;
        }
      } else {
        stableFrameCount.current = 0;
        lastScrollPosition.current = currentPosition;
      }

      requestAnimationFrame(checkScrollStabilized);
    };

    requestAnimationFrame(checkScrollStabilized);
  }, [scrollRef]);

  const onOutlineSelect = useCallback(
    (nodeId: string) => {
      setSelectedOutlineId?.(nodeId);
      beginProgrammaticScroll();
    },
    [setSelectedOutlineId, beginProgrammaticScroll]
  );

  // Stable identities: a fresh array/callback per render would make the
  // tracker re-run its content compare (O(n)) on every streaming re-render.
  const elementIds = useMemo(
    () => allNodesList.map((node) => node.id),
    [allNodesList]
  );
  const outlineIds = useMemo(
    () => new Set(outlineNodeList.map((node) => node.id)),
    [outlineNodeList]
  );
  const onTopElement = useCallback(
    (scrolledId: string) => {
      if (!isProgrammaticScrolling.current) {
        const parentNode = findNearestOutlineAbove(
          scrolledId,
          allNodesList,
          outlineIds
        );
        if (parentNode) {
          setSelectedOutlineId?.(parentNode.id);
        }
      }
    },
    [allNodesList, outlineIds, setSelectedOutlineId]
  );
  useScrollTrack(elementIds, onTopElement, scrollRef, {
    topOffset: scrollTrackOffset,
  });

  return { onOutlineSelect };
}
